// lib/voxide-sdk/VoxideClient.ts
//
// Capability-first Voice AI client. You register the JavaScript functions your
// app can perform; Gemini Live (proxied by the Voxide backend) decides which to
// call from what the user says or types.
//
// This is the in-app copy that powers the dashboard Playground. It is kept in
// lock-step with the published @voxide/react package.

import { WakeWordListener, isWakeWordSupported, type WakeWordFailure } from "./WakeWordListener";
import type {
  VoxideAction,
  VoxideActionConfig,
  VoxideParams,
  VoxideClientConfig,
  VoxideEvent,
  VoxideRemoteConfig,
  VoxideUIConfig,
  VoxideUser,
  ManifestAction,
  ExecutionResult,
  VoxideMessage,
  VoxideStatus,
} from "./types";

const ANON_KEY = "voxide_anonymous_id";

// Last-known dashboard appearance, per publishable key.
//
// Without this the widget's very first paint uses the built-in defaults
// (corner bubble, bottom-right) and only becomes the developer's actual
// configuration once init() returns — a visible morph on every page load.
// Caching lets a returning visitor get the right shape immediately, and the
// widget holds off its first paint entirely when there's nothing cached (see
// `uiHydrated`), so the wrong shape is never shown.
const UI_CACHE_PREFIX = "voxide_ui_";

/** Keys that decide the widget's SHAPE. Only these are worth caching: they're
 *  what a visitor would see change. Cacheing copy (greeting, placeholder…)
 *  would risk showing stale text from a previous config. */
const CACHEABLE_UI_KEYS = [
  "launcherMode", "position", "launcherShape", "launcherSize", "launcherIcon",
  "accentColor", "avatarUrl", "cornerRadius", "skin", "theme",
  // `visualizer` decides whether the launcher paints a GPU canvas or DOM bars.
  // That is a shape decision, so it belongs here: without it a returning
  // visitor gets the waveform on first paint and watches it become an orb.
  //
  // `orb` has to come with it. Caching only the choice and not the look means
  // the first paint renders the default gold-and-violet preset and then morphs
  // to the developer's actual style when init() lands, which is the same flash
  // in a new costume. It is the one large value in this list, still well under
  // a kilobyte of JSON.
  "visualizer", "orb",
  // Changes how wide the bar is, so it is a shape key like the rest.
  "showStatusText",
] as const;

function uiCacheKey(publicKey: string): string {
  return UI_CACHE_PREFIX + publicKey.slice(-12);
}

function readCachedUi(publicKey: string): Record<string, unknown> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(uiCacheKey(publicKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function writeCachedUi(publicKey: string, ui: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  try {
    const slim: Record<string, unknown> = {};
    for (const k of CACHEABLE_UI_KEYS) {
      if (ui[k] !== undefined) slim[k] = ui[k];
    }
    window.localStorage.setItem(uiCacheKey(publicKey), JSON.stringify(slim));
  } catch {
    /* quota/private mode — the widget just falls back to waiting for init() */
  }
}

/** The Voxide hosted backend. Override via config.baseUrl for self-hosting. */
export const DEFAULT_BASE_URL = "https://voxide.onrender.com";

type Middleware = (
  ctx: { name: string; args: Record<string, any>; action: VoxideAction },
  next: (nextArgs?: Record<string, any>) => Promise<void> | void,
  cancel: () => void
) => Promise<void> | void;

function generateAnonId(): string {
  return "anon_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

function readAnonId(): string {
  if (typeof window === "undefined") return generateAnonId();
  try {
    let id = window.localStorage.getItem(ANON_KEY);
    if (!id) {
      id = generateAnonId();
      window.localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return generateAnonId();
  }
}

// ---------------------------------------------------------------------------
// Voice session — see the long comment above the fields below for why this
// lives on the client instead of in a React hook.
// ---------------------------------------------------------------------------

/** Snapshot of the live voice/text session, read via useSyncExternalStore. */
export interface VoxideVoiceSnapshot {
  status: VoxideStatus;
  messages: VoxideMessage[];
  currentAction: string | null;
  /** True while the wake-word listener is active. Tracked separately from
   *  `status` because the widget needs to keep showing an "armed" affordance
   *  during the brief connecting/listening handoff after a phrase is heard. */
  wakeArmed?: boolean;
  /** Set when arming failed, so the widget can explain itself instead of
   *  silently doing nothing. */
  wakeFailure?: WakeWordFailure;
  /** Set when `status` is "error" and the server told us why in a way the UI
   *  should treat specially — currently only "usage_limit" (the plan's
   *  sessions ran out mid-connection, as opposed to a generic network/setup
   *  failure). Undefined for any other kind of error. */
  errorCode?: string;
  /** Row id of the live session, sent by the server in its "ready" frame.
   *  Null between sessions. Used to attach an end-of-call rating to the exact
   *  conversation it refers to. */
  sessionId?: string | null;
  /** Set once a session that contained a real exchange has ended, and stays
   *  set until the visitor rates it or dismisses the prompt. This is what the
   *  widget watches to decide whether to show the star rating — a session that
   *  connected and closed without anyone saying anything never lands here, so
   *  an accidental open can't produce a feedback prompt. */
  endedSession?: EndedSession | null;
}

/** A finished conversation awaiting (optional) feedback from the visitor. */
export interface EndedSession {
  id: string;
  /** Finalized turns exchanged, used only to describe the conversation in UI. */
  turns: number;
}

const EMPTY_VOICE_SNAPSHOT: VoxideVoiceSnapshot = {
  status: "idle",
  messages: [],
  currentAction: null,
  errorCode: undefined,
  wakeArmed: false,
  wakeFailure: undefined,
  sessionId: null,
  endedSession: null,
};

const floatTo16BitPCM = (float32Array: Float32Array): ArrayBuffer => {
  const buffer = new ArrayBuffer(float32Array.length * 2);
  const view = new DataView(buffer);
  for (let i = 0; i < float32Array.length; i++) {
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buffer;
};

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
};

// FFT window for both analysers. 512 gives 256 frequency bins, enough for a
// spectrum visualizer to show something other than four fat blobs, and still
// nothing next to the cost of the ScriptProcessor already on this thread.
const ANALYSER_FFT_SIZE = 512;

// RMS (0–1) of an analyser's current time-domain buffer. Cheap enough to call
// every animation frame; the caller is responsible for not doing that during render.
//
// `buf` must be fftSize long, not frequencyBinCount. getByteTimeDomainData
// fills a window's worth of samples and silently writes only as many as the
// array holds, so a half-length buffer measures RMS over half the window. That
// was the shape of the bug this comment replaces.
function readAnalyserRMS(analyser: AnalyserNode | null, buf: Uint8Array<ArrayBuffer> | null): number {
  if (!analyser || !buf) return 0;
  analyser.getByteTimeDomainData(buf);
  let sumSquares = 0;
  for (let i = 0; i < buf.length; i++) {
    const v = (buf[i] - 128) / 128;
    sumSquares += v * v;
  }
  return Math.sqrt(sumSquares / buf.length);
}

// Normalised frequency magnitudes (0–1) into a caller-owned array. Writes at
// most `out.length` bins and returns how many it filled, so a visualizer can
// keep one array across frames and never allocate.
function readAnalyserSpectrum(
  analyser: AnalyserNode | null,
  buf: Uint8Array<ArrayBuffer> | null,
  out: Float32Array,
): number {
  if (!analyser || !buf) return 0;
  analyser.getByteFrequencyData(buf);
  const bins = Math.min(out.length, analyser.frequencyBinCount, buf.length);
  for (let i = 0; i < bins; i++) out[i] = buf[i] / 255;
  return bins;
}

/** Turn a backend base URL into a ws(s):// live endpoint. `visitorId`, when
 *  given, is attached as `?anon=` so the server can seed a fresh connection
 *  with this same visitor's recent conversation history (see
 *  voxide-ws-server's getRecentHistory). */
export function resolveLiveUrl(baseUrl: string | undefined, publicKey: string, visitorId?: string): string {
  const anonParam = visitorId ? `&anon=${encodeURIComponent(visitorId)}` : "";
  // Same-origin fallback (no baseUrl configured): use the page's own host.
  if (!baseUrl && typeof window !== "undefined") {
    const wsProto = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${wsProto}//${window.location.host}/api/sdk/live?key=${publicKey}${anonParam}`;
  }
  const url = baseUrl || "";
  // Derive scheme from the backend URL's own protocol — this is the fix.
  const isSecure = url.startsWith("https://") || url.startsWith("wss://");
  const host = url.replace(/^https?:\/\//, "").replace(/^wss?:\/\//, "").replace(/\/+$/, "");
  return `${isSecure ? "wss:" : "ws:"}//${host}/api/sdk/live?key=${publicKey}${anonParam}`;
}

// A stable id for this browser tab's visit, used only to let a fresh WS
// connection be seeded with this same visitor's recent conversation history
// server-side — never sent anywhere else, never used for tracking/identity.
// sessionStorage (not localStorage) deliberately: it should span reconnects
// and page navigation within the same tab, but not linger across visits.
function getVisitorId(): string | undefined {
  if (typeof window === "undefined" || !window.sessionStorage) return undefined;
  const KEY = "__voxide_visitor_id";
  try {
    let id = window.sessionStorage.getItem(KEY);
    if (!id) {
      id = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      window.sessionStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    return undefined; // storage disabled/blocked (private mode, strict cookie settings, etc.)
  }
}

export class VoxideClient {
  publicKey: string;
  baseUrl: string;
  ui: VoxideUIConfig;
  /**
   * UI the developer set in code, kept apart from everything else.
   *
   * `ui` is the resolved view and gets rebuilt whenever a better source of
   * truth arrives. This is the only layer that must survive that rebuild, so it
   * cannot live in `ui` alone: merging the first-paint cache into `ui` is what
   * made a stale cached value outrank the live dashboard config indefinitely.
   */
  private _codeUi: VoxideUIConfig;

  // Capability registry
  actions: Map<string, VoxideAction> = new Map();
  private middlewares: Middleware[] = [];

  // State
  private state: Record<string, any> = {};
  private stateBinders: Record<string, () => any> = {};
  private stateGetters: Record<string, () => any> = {};

  // Identity
  user: VoxideUser | null = null;
  anonymousId: string;

  // Localization
  language: string | null;
  languageMode: "strict" | "adaptive" | "manual" = "strict";
  supportedLanguages: string[] = [];

  // UX hooks
  confirmationHandler:
    | ((action: VoxideAction, args: Record<string, any>) => boolean | Promise<boolean>)
    | null = null;
  fallback: { message: string; suggestions: string[] } | null = null;

  // Routing
  private activeScopes: Set<string> = new Set(["global"]);

  // Lifecycle
  isInitialized = false;
  /** True once the widget's shape-critical appearance is known — from the
   *  localStorage cache, from explicit constructor config, or from init().
   *  <VoxideWidget> holds its first paint until this is true so a visitor
   *  never sees the built-in default launcher morph into the real one. */
  uiHydrated = false;
  /** Set just before the page goes away, so the teardown that follows is
   *  treated as "the page died", not "the visitor finished a conversation".
   *  Without this, unloading fires disconnect() → the session is marked ended →
   *  the rating prompt flashes on screen for the instant before the document
   *  is destroyed. It can't be answered and it looks like a glitch. */
  _unloading = false;
  agentConfig: VoxideRemoteConfig | null = null;

  private _listeners: Record<VoxideEvent, Array<(payload: any) => void>> = {
    action: [],
    status: [],
    message: [],
    transcript: [],
    ready: [],
    error: [],
  };

  // ---------------------------------------------------------------------
  // Voice session state. Owned by the client (not a React hook) so an active
  // call survives a <VoxideWidget> remount — dev HMR, a theme-driven
  // re-render higher in the tree, a developer mounting the widget somewhere
  // that isn't perfectly stable — none of that should hang up a live call.
  // Only an explicit disconnect (user closes the widget, or destroy()) ends it.
  // ---------------------------------------------------------------------
  private _voiceWs: WebSocket | null = null;
  private _voiceAudioIn: AudioContext | null = null;
  private _voiceAudioOut: AudioContext | null = null;
  private _voiceMic: MediaStream | null = null;
  private _voiceProcessor: ScriptProcessorNode | null = null;
  private _voiceNextPlay = 0;
  private _voiceActiveSources: AudioBufferSourceNode[] = [];
  private _voiceInputAnalyser: AnalyserNode | null = null;
  private _voiceInputBuf: Uint8Array<ArrayBuffer> | null = null;
  private _voiceOutputAnalyser: AnalyserNode | null = null;
  private _voiceOutputBuf: Uint8Array<ArrayBuffer> | null = null;
  private _voicePendingUserText = "";
  private _voicePendingAiText = "";
  private _voiceSnapshot: VoxideVoiceSnapshot = EMPTY_VOICE_SNAPSHOT;
  private _voiceSubscribers: Set<() => void> = new Set();

  // Wake word ("hey acme"). Lives outside the voice-session audio graph
  // entirely: it holds its own microphone via the Web Speech API and opens no
  // WebSocket, so nothing is billed until a phrase actually matches.
  private _wakeListener: WakeWordListener | null = null;

  constructor(config: VoxideClientConfig) {
    if (!config?.publicKey) {
      throw new Error("[Voxide SDK] publicKey is required.");
    }
    if (!config.publicKey.startsWith("vox_pub_")) {
      console.warn(
        "[Voxide SDK] publicKey does not look like a Voxide publishable key (expected vox_pub_...)."
      );
    }

    this.publicKey = config.publicKey;
    this.baseUrl = (config.baseUrl || DEFAULT_BASE_URL).replace(/\/+$/, "");
    this.language = config.language || null;
    this._codeUi = config.ui || {};
    this.ui = { ...this._codeUi };

    // Seed shape-critical appearance from the last visit so the first paint is
    // already correct. This is a placeholder for the round trip and nothing
    // more: init() rebuilds `ui` from `_codeUi` and the live config, and the
    // cache is not consulted again. It used to be folded into `ui` here, which
    // made it survive that rebuild and silently outrank the dashboard forever,
    // so changing position or launcher mode did nothing for anyone who had
    // loaded the widget before.
    const cached = readCachedUi(config.publicKey);
    if (cached) {
      this.ui = { ...cached, ...this._codeUi };
      this.uiHydrated = true;
    }
    // Code that pins the shape outright doesn't need to wait for the network
    // either — there's nothing init() could change about how it looks.
    if (this.ui.launcherMode !== undefined && this.ui.position !== undefined) {
      this.uiHydrated = true;
    }

    this.anonymousId = readAnonId();
  }

  // -------------------------------------------------------------------------
  //  CAPABILITY REGISTRATION
  // -------------------------------------------------------------------------
  register(actionsConfig: Record<string, VoxideActionConfig>): this {
    for (const [name, config] of Object.entries(actionsConfig)) {
      if (!config.description) {
        throw new Error(`[Voxide SDK] Action '${name}' requires a 'description'.`);
      }
      this.actions.set(name, {
        name,
        description: config.description,
        params: config.params || {},
        handler: config.handler || null,
        scope: config.scope || "global",
        dangerous: !!config.dangerous,
        requireConfirmation: !!config.requireConfirmation || !!config.dangerous,
      });
    }
    return this;
  }

  unregister(name: string): this {
    this.actions.delete(name);
    return this;
  }

  use(middleware: Middleware): this {
    if (typeof middleware !== "function") {
      throw new Error("[Voxide SDK] Middleware must be a function (ctx, next, cancel) => any");
    }
    this.middlewares.push(middleware);
    return this;
  }

  // -------------------------------------------------------------------------
  //  STATE
  // -------------------------------------------------------------------------
  setState(newState: Record<string, any>): this {
    this.state = { ...this.state, ...newState };
    return this;
  }

  bindState(getter: () => Record<string, any>): this {
    if (typeof getter !== "function") {
      throw new Error("[Voxide SDK] bindState requires a function returning an object.");
    }
    this.stateBinders["__bind__"] = getter;
    return this;
  }

  registerState(getters: Record<string, () => any>): this {
    for (const [key, fn] of Object.entries(getters)) {
      if (typeof fn !== "function") continue;
      this.stateGetters[key] = fn;
    }
    return this;
  }

  _getCurrentStateSnapshot(): Record<string, any> {
    const dynamic: Record<string, any> = {};
    if (this.stateBinders.__bind__) {
      try {
        Object.assign(dynamic, this.stateBinders.__bind__() || {});
      } catch (e) {
        console.error("[Voxide SDK] bindState getter threw:", e);
      }
    }
    for (const [key, fn] of Object.entries(this.stateGetters)) {
      try {
        dynamic[key] = fn();
      } catch (e) {
        console.error(`[Voxide SDK] stateGetter '${key}' threw:`, e);
      }
    }
    return { ...this.state, ...dynamic };
  }

  // -------------------------------------------------------------------------
  //  IDENTITY
  // -------------------------------------------------------------------------
  setUser(user: VoxideUser | null): this {
    this.user = user || null;
    return this;
  }

  getIdentity() {
    return { anonymousId: this.anonymousId, user: this.user };
  }

  // -------------------------------------------------------------------------
  //  LOCALIZATION
  // -------------------------------------------------------------------------
  setLanguage(lang: string): this {
    this.language = lang;
    return this;
  }

  enableMultilingual({
    mode = "adaptive",
    supported = [],
  }: { mode?: "strict" | "adaptive" | "manual"; supported?: string[] } = {}): this {
    this.languageMode = mode;
    this.supportedLanguages = supported;
    return this;
  }

  // -------------------------------------------------------------------------
  //  UX HOOKS
  // -------------------------------------------------------------------------
  setFallback({ message, suggestions = [] }: { message: string; suggestions?: string[] }): this {
    this.fallback = { message, suggestions };
    return this;
  }

  onConfirmation(
    handler: (action: VoxideAction, args: Record<string, any>) => boolean | Promise<boolean>
  ): this {
    this.confirmationHandler = handler;
    return this;
  }

  /** Sets widget UI defaults after construction. */
  configureUI(ui: VoxideUIConfig): this {
    // Recorded as code-set, not just merged into the resolved view, or init()
    // would discard it the moment the dashboard config lands.
    this._codeUi = { ...this._codeUi, ...ui };
    this.ui = { ...this.ui, ...ui };
    return this;
  }

  // ── Driving a session without React ────────────────────────────────────────
  //
  // These are the public names for what `useVoxideVoice` has always called
  // internally. Until now the only supported way to start a call was to render
  // <VoxideWidget> or use the hook, so anyone building their own UI had to
  // reach for `_voiceConnect` and friends.
  //
  // That was tolerable while React was the only audience. It stops being
  // tolerable the moment the docs tell a Django developer to write an
  // underscore: a documented private method is a public method with a warning
  // label, and we would be bound to the name anyway.
  //
  // Every one of these is a straight delegation. The underscore methods are
  // unchanged and the hook still calls them, so nothing that works today takes
  // a different path.

  /** Start a voice session. Resolves once connected. */
  connect(): Promise<void> {
    return this._voiceConnect();
  }

  /** End the current voice session. */
  disconnect(): void {
    this._voiceDisconnect();
  }

  /** Send a typed message into the same session the voice uses. */
  sendText(text: string): Promise<void> {
    return this._voiceSendText(text);
  }

  /** Stop the agent mid-sentence, as a barge-in would. */
  interrupt(): void {
    this._voiceInterrupt();
  }

  /**
   * The current session state: status, messages, the action in flight.
   *
   * Cheap and synchronous. Pair it with `subscribe` to drive your own UI.
   */
  getSnapshot(): VoxideVoiceSnapshot {
    return this._getVoiceSnapshot();
  }

  /** Called on every session change. Returns an unsubscribe function. */
  subscribe(onChange: () => void): () => void {
    return this._subscribeVoice(onChange);
  }

  /**
   * Live microphone level, 0 to 1.
   *
   * Not reactive. Poll it from a requestAnimationFrame loop if you are drawing
   * a meter; reading it during a render will not update on its own.
   */
  getInputLevel(): number {
    return this._voiceInputLevel();
  }

  /** Live level of the agent's own speech, 0 to 1. Poll it, as above. */
  getOutputLevel(): number {
    return this._voiceOutputLevel();
  }

  /** Whether this browser can run the wake word at all. */
  isWakeWordAvailable(): boolean {
    return this._wakeAvailable();
  }

  /** Start listening locally for the wake phrase. No session, nothing billed. */
  armWakeWord(): void {
    this._wakeArm();
  }

  /** Stop listening for the wake phrase. */
  disarmWakeWord(): void {
    this._wakeDisarm(false);
  }

  /**
   * Register a `navigate` capability.
   *
   * Pass your app's real routes as the second argument. Without them the model
   * has to *guess* a path from what the user said, so "take me to the near-me
   * page" becomes `/nearMe` when your route is actually `/near` — a 404. Given
   * the list, the model is constrained to your exact paths (and the handler
   * refuses anything off the list rather than navigating to a dead page).
   *
   * ```ts
   * ai.enableNavigation(router, [
   *   { path: "/near",  description: "Spots near the user" },
   *   { path: "/saved", description: "The user's saved spots" },
   * ]);
   * // or the shorthand:  { "/near": "Spots near me", "/saved": "Saved spots" }
   * ```
   */
  enableNavigation(
    router?: { push?: (route: string) => void },
    routes?: Array<{ path: string; description?: string }> | Record<string, string>,
  ): this {
    const routeList = routes
      ? Array.isArray(routes)
        ? routes
        : Object.entries(routes).map(([path, description]) => ({ path, description }))
      : [];
    const validPaths = new Set(routeList.map((r) => r.path));

    let description: string;
    const routeParam: VoxideParams = {};

    if (routeList.length > 0) {
      const listing = routeList
        .map((r) => `- ${r.path}${r.description ? `: ${r.description}` : ""}`)
        .join("\n");
      description =
        "Navigate the user to a page in this app. These are the ONLY valid paths, " +
        "use them exactly as written and never invent or guess a path:\n" +
        listing +
        "\nIf the user asks for a page that isn't listed, tell them it doesn't exist instead of navigating.";
      // enum hard-constrains the model to real paths (passed through to the
      // model's function schema by the backend).
      routeParam.route = {
        type: "string",
        required: true,
        description: "The exact path to open. Must be one of the app's known paths.",
        enum: routeList.map((r) => r.path),
      };
    } else {
      // No routes declared, keep the old free-form behaviour but discourage
      // guessing, which is the whole cause of wrong-path navigations.
      description =
        "Navigate the user to a different page in the app. Only use a path you are certain exists; " +
        "if you are unsure of the exact path, ask the user rather than guessing.";
      routeParam.route = { type: "string", required: true };
    }

    this.register({
      navigate: {
        description,
        params: routeParam,
        handler: ({ route }) => {
          // Even with the enum, refuse an unknown path rather than 404 the user.
          if (routeList.length > 0 && !validPaths.has(route)) {
            return { status: "error", message: `No such page: ${route}. Tell the user that page doesn't exist.` };
          }
          if (router?.push) router.push(route);
          else if (typeof window !== "undefined") window.location.href = route;
          return { status: "navigated", route };
        },
      },
    });
    return this;
  }

  // -------------------------------------------------------------------------
  //  ROUTE / SCOPE
  // -------------------------------------------------------------------------
  setActiveRoute(route: string): this {
    this.activeScopes = new Set(["global", route]);
    for (const [, action] of this.actions.entries()) {
      if (action.scope.endsWith("/*")) {
        const prefix = action.scope.slice(0, -2);
        if (route?.startsWith(prefix)) this.activeScopes.add(action.scope);
      }
    }
    return this;
  }

  _getActiveActions(): VoxideAction[] {
    const out: VoxideAction[] = [];
    for (const action of this.actions.values()) {
      if (action.scope === "global" || this.activeScopes.has(action.scope)) {
        out.push(action);
      }
    }
    return out;
  }

  // -------------------------------------------------------------------------
  //  EVENT EMITTER
  // -------------------------------------------------------------------------
  on(event: VoxideEvent, handler: (payload: any) => void): () => void {
    if (!this._listeners[event]) this._listeners[event] = [];
    this._listeners[event].push(handler);
    return () => {
      this._listeners[event] = this._listeners[event].filter((h) => h !== handler);
    };
  }

  _emit(event: VoxideEvent, payload: any): void {
    for (const handler of this._listeners[event] || []) {
      try {
        handler(payload);
      } catch (e) {
        console.error(e);
      }
    }
  }

  // -------------------------------------------------------------------------
  //  MANIFEST
  // -------------------------------------------------------------------------
  _getManifestActions(): ManifestAction[] {
    return Array.from(this.actions.values()).map((a) => ({
      name: a.name,
      description: a.description,
      params: a.params,
      scope: a.scope,
      dangerous: a.dangerous,
    }));
  }

  private detectEnvironment(): "development" | "production" {
    if (typeof window === "undefined") return "production";
    const host = window.location.hostname;
    return host === "localhost" || host === "127.0.0.1" ? "development" : "production";
  }

  // -------------------------------------------------------------------------
  //  INIT
  // -------------------------------------------------------------------------
  async init(): Promise<this> {
    if (this.isInitialized) return this;

    const initRes = await fetch(`${this.baseUrl}/api/sdk/init`, {
      method: "GET",
      headers: { Authorization: `Bearer ${this.publicKey}` },
    });

    if (!initRes.ok) {
      // Surface a structured `code` (e.g. "usage_limit") alongside the message
      // so <VoxideWidget> can render a purpose-built state for it — a friendly
      // "taking a break, developer please refill your session" message rather
      // than a generic connection-error screen. The widget is embedded on the
      // developer's own site, so this IS seen by their end users; the copy is
      // written to make sense to both audiences at once (see
      // VoxideWidget's DEFAULT_USAGE_LIMIT_MESSAGE), and is fully overridable
      // via `ui.usageLimitMessage`.
      let code: string | undefined;
      let message = "Could not connect to the assistant.";
      try {
        const body = await initRes.clone().json();
        code = typeof body?.error === "string" ? body.error : undefined;
        message = body?.message || message;
      } catch {
        message = await initRes.text().catch(() => initRes.statusText) || message;
      }
      const err = new Error(message) as Error & { code?: string };
      err.code = code;
      throw err;
    }

    const initData = await initRes.json();
    this.agentConfig = initData.config;
    if (!this.language) {
      this.language = this.agentConfig?.agent?.language || "en-US";
    }

    // Fold dashboard appearance in as UI defaults. Anything set explicitly in
    // the constructor `ui` still wins (spread last), so code overrides the
    // dashboard, and the dashboard overrides the built-in defaults. Null/empty
    // fields from the backend are dropped so they don't clobber real defaults.
    const appearance = this.agentConfig?.appearance;
    if (appearance) {
      const clean = Object.fromEntries(
        Object.entries(appearance).filter(([, v]) => v !== null && v !== undefined && v !== "")
      );
      // Code outranks the dashboard on purpose: a value pinned in a build
      // should not change under the developer's feet. But it fails silently,
      // and a developer watching the Appearance tab do nothing has no way to
      // know why. Say so, once, naming the exact key.
      //
      // This is not a style nit. It has cost real hours twice: a hardcoded
      // `position` made the dashboard's placement control look broken on two
      // different sites, and nothing anywhere reported it.
      for (const [key, remote] of Object.entries(clean)) {
        const pinned = (this._codeUi as Record<string, unknown>)[key];
        if (pinned === undefined) continue;
        if (JSON.stringify(pinned) === JSON.stringify(remote)) continue;
        console.warn(
          `[Voxide] "${key}" is set in your code to ${JSON.stringify(pinned)}, ` +
            `so the dashboard's value ${JSON.stringify(remote)} is ignored. ` +
            `Remove it from your VoxideClient config or <VoxideWidget> props to ` +
            `control this from the dashboard.`,
        );
      }

      // Rebuilt from `_codeUi`, not from `this.ui`. Merging into `this.ui`
      // carried the first-paint cache through and let it win, which is the bug
      // 0.7.4 fixed: the dashboard could never change a key a visitor had
      // already cached.
      this.ui = { ...clean, ...this._codeUi };
      // Remember it for the next page load, so this round trip never again
      // costs a visible change of shape.
      writeCachedUi(this.publicKey, clean as Record<string, unknown>);
    }
    this.uiHydrated = true;

    // Fire-and-forget manifest sync — the SDK still runs on the last known
    // manifest if this fails (e.g. the production manifest is locked).
    try {
      const manifestRes = await fetch(`${this.baseUrl}/api/sdk/manifest`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.publicKey}`,
        },
        body: JSON.stringify({
          actions: this._getManifestActions(),
          stateSchema: [
            ...Object.keys(this.state),
            ...Object.keys(this.stateBinders),
            ...Object.keys(this.stateGetters),
          ],
          environment: this.detectEnvironment(),
        }),
      });
      if (!manifestRes.ok) {
        console.warn("[Voxide SDK] Manifest sync skipped; running with last known manifest.");
      }
    } catch (e) {
      console.warn("[Voxide SDK] Manifest sync failed:", e);
    }

    this.isInitialized = true;
    this._emit("ready", this.agentConfig);
    return this;
  }

  // -------------------------------------------------------------------------
  //  EXECUTION
  // -------------------------------------------------------------------------
  async _executeAction(actionName: string, args: Record<string, any> = {}): Promise<ExecutionResult> {
    const action = this.actions.get(actionName);

    if (!action) {
      return { status: "error", message: `Action '${actionName}' is not registered.` };
    }

    // Required-param validation → ask the model to gather missing fields.
    const missing: string[] = [];
    for (const [key, rules] of Object.entries(action.params || {})) {
      if (rules.required && (args[key] === undefined || args[key] === null || args[key] === "")) {
        missing.push(key);
      }
    }
    if (missing.length > 0) {
      return {
        status: "clarification_needed",
        missing_fields: missing,
        message: `SYSTEM: Cannot execute. Ask the user for: ${missing.join(", ")}.`,
      };
    }

    // Confirmation gate for dangerous actions.
    if (action.requireConfirmation) {
      const ok = this.confirmationHandler
        ? await this.confirmationHandler(action, args)
        : typeof window !== "undefined" && window.confirm(`Confirm: ${action.description}`);
      if (!ok) {
        return { status: "cancelled", message: "User cancelled the action." };
      }
    }

    // Middleware chain (validation, logging, blocking).
    let cancelled = false;
    let mutatedArgs = args;
    const runMiddleware = async (idx: number): Promise<void> => {
      if (idx >= this.middlewares.length) return;
      await this.middlewares[idx](
        { name: actionName, args: mutatedArgs, action },
        (nextArgs) => {
          if (nextArgs) mutatedArgs = nextArgs;
          return runMiddleware(idx + 1);
        },
        () => {
          cancelled = true;
        }
      );
    };
    try {
      await runMiddleware(0);
    } catch (e: any) {
      return { status: "error", message: `Middleware threw: ${e.message}` };
    }
    if (cancelled) {
      return { status: "blocked", message: "Action blocked by middleware." };
    }

    this._emit("action", { name: actionName, args: mutatedArgs });

    if (!action.handler) {
      return { status: "noop", message: "No handler bound for this action." };
    }

    try {
      const result = await action.handler(mutatedArgs);
      return { status: "success", result: result !== undefined ? result : "Action executed." };
    } catch (error: any) {
      console.error(`[Voxide SDK] Handler '${actionName}' threw:`, error);
      return { status: "error", message: error.message };
    }
  }

  // -------------------------------------------------------------------------
  //  VOICE SESSION
  //  React binding lives in useVoxideVoice.ts (a thin useSyncExternalStore
  //  wrapper). Everything stateful — the WebSocket, audio graph, transcript —
  //  lives here so it's independent of any particular component's lifecycle.
  // -------------------------------------------------------------------------
  _subscribeVoice(cb: () => void): () => void {
    this._voiceSubscribers.add(cb);
    return () => {
      this._voiceSubscribers.delete(cb);
    };
  }

  _getVoiceSnapshot(): VoxideVoiceSnapshot {
    return this._voiceSnapshot;
  }

  // ---- End-of-conversation feedback ---------------------------------------

  /**
   * Submit the visitor's 1–5 star rating (and optional comment) for the
   * conversation that just ended. Resolves to true when it was recorded.
   *
   * Safe to call twice for the same conversation: the widget sends the star as
   * soon as it's tapped — so a rating is never lost when someone walks away
   * without writing anything — and sends again if they go on to add a comment.
   * The server upserts on session id, so the second call updates the first row
   * rather than being rejected as a duplicate.
   *
   * Never throws, and never dismisses the prompt itself: this is a courtesy
   * ask on someone else's website, so a network blip must not surface an
   * exception into the host page, and closing the prompt is the widget's
   * decision (it still has a comment box to offer).
   */
  async submitFeedback(rating: number, comment?: string): Promise<boolean> {
    const ended = this._voiceSnapshot.endedSession;
    if (!ended) return false;
    if (!Number.isInteger(rating) || rating < 1 || rating > 5) return false;

    try {
      const res = await fetch(`${this.baseUrl}/api/sdk/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.publicKey}`,
        },
        body: JSON.stringify({
          sessionId: ended.id,
          rating,
          comment: comment?.trim() || undefined,
        }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Clear the pending feedback prompt without submitting anything. */
  dismissFeedback(): void {
    if (this._voiceSnapshot.endedSession) {
      this._setVoiceSnapshot({ endedSession: null });
    }
  }

  private _setVoiceSnapshot(patch: Partial<VoxideVoiceSnapshot>): void {
    this._voiceSnapshot = { ...this._voiceSnapshot, ...patch };
    this._voiceSubscribers.forEach((cb) => cb());
  }

  private _setVoiceStatus(next: VoxideStatus): void {
    if (next === this._voiceSnapshot.status) return;
    this._setVoiceSnapshot({ status: next });
    this._emit("status", next); // public, consumer-facing (client.on("status", ...))
  }

  /** Ends whatever AI text is currently accumulating and finalizes its bubble,
   *  so the next "text" chunk to arrive starts a brand new one instead of
   *  silently concatenating onto this one. Call this at every point that
   *  marks a real boundary in the AI's speech — end of turn, about to call a
   *  tool, or a new user message arriving — even if the server's own
   *  turnComplete signal hasn't (yet, or ever) shown up for it. */
  private _flushPendingAiText(): void {
    if (this._voicePendingAiText) {
      this._emit("message", { role: "ai", text: this._voicePendingAiText });
      this._voicePendingAiText = "";
    }
    this._setVoiceSnapshot({ messages: finalizePartial(this._voiceSnapshot.messages, "ai") });
  }

  _voiceInputLevel(): number {
    return readAnalyserRMS(this._voiceInputAnalyser, this._voiceInputBuf);
  }

  _voiceOutputLevel(): number {
    return readAnalyserRMS(this._voiceOutputAnalyser, this._voiceOutputBuf);
  }

  /** Mic frequency bins into `out`. Returns how many were written. */
  _voiceInputSpectrum(out: Float32Array): number {
    return readAnalyserSpectrum(this._voiceInputAnalyser, this._voiceInputBuf, out);
  }

  /** Agent-speech frequency bins into `out`. Returns how many were written. */
  _voiceOutputSpectrum(out: Float32Array): number {
    return readAnalyserSpectrum(this._voiceOutputAnalyser, this._voiceOutputBuf, out);
  }

  private _voiceStopPlayback(): void {
    for (const src of this._voiceActiveSources) {
      try {
        src.stop();
      } catch {}
    }
    this._voiceActiveSources = [];
    if (this._voiceAudioOut) {
      try {
        this._voiceAudioOut.close();
      } catch {}
      this._voiceAudioOut = null;
    }
    this._voiceOutputAnalyser = null;
    this._voiceOutputBuf = null;
    this._voiceNextPlay = 0;
  }

  private _voicePlayAudioChunk(base64Data: string): void {
    if (!this._voiceAudioOut) {
      this._voiceAudioOut = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
    }
    const ctx = this._voiceAudioOut;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    if (!this._voiceOutputAnalyser) {
      const analyser = ctx.createAnalyser();
      analyser.fftSize = ANALYSER_FFT_SIZE;
      analyser.connect(ctx.destination);
      this._voiceOutputAnalyser = analyser;
      // fftSize, not frequencyBinCount: this buffer serves both the time-domain
      // read (which needs a full window) and the frequency read (which uses the
      // first half). See readAnalyserRMS.
      this._voiceOutputBuf = new Uint8Array(analyser.fftSize);
    }
    try {
      const binary = window.atob(base64Data);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const int16 = new Int16Array(bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength));
      const float32 = new Float32Array(int16.length);
      for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 32768.0;
      const buffer = ctx.createBuffer(1, float32.length, 24000);
      buffer.copyToChannel(float32, 0);

      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(this._voiceOutputAnalyser);

      const now = ctx.currentTime;
      const startAt = this._voiceNextPlay < now ? now : this._voiceNextPlay;
      source.start(startAt);
      this._voiceNextPlay = startAt + buffer.duration;
      this._voiceActiveSources.push(source);

      source.onended = () => {
        this._voiceActiveSources = this._voiceActiveSources.filter((s) => s !== source);
        if (this._voiceActiveSources.length === 0 && this._voiceSnapshot.status === "speaking") {
          this._setVoiceStatus("listening");
        }
      };
    } catch (e) {
      console.error("[Voxide Playback] Error:", e);
    }
  }

  private async _voiceStartMic(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 16000 },
      });
      if (this._voiceWs?.readyState !== WebSocket.OPEN) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      if (!this._voiceAudioOut) {
        this._voiceAudioOut = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
      }
      if (this._voiceAudioOut.state === "suspended") await this._voiceAudioOut.resume();

      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 16000 });
      if (audioCtx.state === "suspended") await audioCtx.resume();

      this._voiceAudioIn = audioCtx;
      this._voiceMic = stream;

      const source = audioCtx.createMediaStreamSource(stream);
      const processor = audioCtx.createScriptProcessor(2048, 1, 1);
      this._voiceProcessor = processor;
      processor.onaudioprocess = (e) => {
        if (this._voiceWs?.readyState !== WebSocket.OPEN) return;
        const float32 = e.inputBuffer.getChannelData(0);
        const pcm16 = floatTo16BitPCM(float32);
        const b64 = arrayBufferToBase64(pcm16);
        this._voiceWs.send(JSON.stringify({ type: "audio_input", data: b64 }));
      };
      source.connect(processor);
      processor.connect(audioCtx.destination);

      // Passive tap for live mic-level metering (doesn't touch the send path above).
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = ANALYSER_FFT_SIZE;
      source.connect(analyser);
      this._voiceInputAnalyser = analyser;
      // fftSize, not frequencyBinCount. See readAnalyserRMS.
      this._voiceInputBuf = new Uint8Array(analyser.fftSize);
    } catch (err) {
      console.error("[Voxide Mic] Failed to start:", err);
      this._setVoiceStatus("error");
    }
  }

  _voiceDisconnect(): void {
    this._voiceStopPlayback();
    if (this._voiceProcessor) {
      try {
        this._voiceProcessor.disconnect();
      } catch {}
      this._voiceProcessor = null;
    }
    if (this._voiceAudioIn) {
      try {
        this._voiceAudioIn.close();
      } catch {}
      this._voiceAudioIn = null;
    }
    this._voiceInputAnalyser = null;
    this._voiceInputBuf = null;
    if (this._voiceMic) {
      this._voiceMic.getTracks().forEach((t) => t.stop());
      this._voiceMic = null;
    }
    if (this._voiceWs) {
      try {
        this._voiceWs.close();
      } catch {}
      this._voiceWs = null;
    }
    this._voicePendingUserText = "";
    this._voicePendingAiText = "";

    // Close out the session for feedback purposes. Only a conversation with a
    // real exchange (the visitor said something AND the agent replied) is worth
    // asking about — connecting and immediately closing, or a session that only
    // ever heard the greeting, must not trigger a rating prompt.
    const endingId = this._voiceSnapshot.sessionId;
    if (endingId && !this._voiceSnapshot.endedSession && !this._unloading) {
      const msgs = this._voiceSnapshot.messages;
      const hasUser = msgs.some((m) => m.role === "user" && m.text.trim());
      const hasAi = msgs.some((m) => m.role === "ai" && m.text.trim());
      if (hasUser && hasAi) {
        this._setVoiceSnapshot({ endedSession: { id: endingId, turns: msgs.length } });
      }
    }
    this._setVoiceSnapshot({ sessionId: null, currentAction: null });
    this._setVoiceStatus("idle");

    // Return to ambient listening after a conversation ends, so the visitor can
    // start the next one the same way they started this one. Skipped when the
    // listener was never armed, so a click-to-talk user is never opted in.
    if (this._wakeWasArmed) this._wakeArm();
  }

  // -------------------------------------------------------------------------
  //  WAKE WORD
  //  Detection is entirely local (see WakeWordListener). Its only job is to
  //  decide when to call _voiceConnect() — the same funnel a click uses.
  // -------------------------------------------------------------------------

  /** Whether the listener was running before the current session, so
   *  _voiceDisconnect() knows whether re-arming is wanted. */
  private _wakeWasArmed = false;

  /** The dashboard's wake config, or null when the feature is off/unavailable
   *  for this project. `/api/sdk/init` omits it entirely unless the owner's
   *  plan allows it, so there's no plan check to repeat here. */
  _wakeConfig(): { phrase: string; autoArm: boolean } | null {
    const cfg = this.agentConfig?.wakeWord;
    if (!cfg?.enabled || !cfg.phrase) return null;
    return { phrase: cfg.phrase, autoArm: cfg.autoArm !== false };
  }

  /** True when this project wants a wake word AND this browser can do it.
   *  The widget uses this to decide whether to show any wake-word UI at all —
   *  on Firefox it stays entirely click-to-talk, exactly as before. */
  _wakeAvailable(): boolean {
    return this._wakeConfig() !== null && isWakeWordSupported();
  }

  _wakeArm(): void {
    if (!this.isInitialized || this._wakeListener?.isRunning) return;
    const cfg = this._wakeConfig();
    if (!cfg || !isWakeWordSupported()) return;
    // Never listen for a trigger while a conversation is already open — the
    // session owns the microphone, and re-triggering mid-call is meaningless.
    const status = this._voiceSnapshot.status;
    if (status !== "idle" && status !== "error" && status !== "armed") return;

    this._wakeListener?.destroy();
    this._wakeListener = new WakeWordListener({
      phrase: cfg.phrase,
      lang: this.language || "en-US",
      onDetected: () => {
        // The listener has already released the microphone by this point;
        // _voiceConnect() is then free to getUserMedia without contention.
        this._setVoiceSnapshot({ wakeArmed: false });
        this._voiceConnect();
      },
      onFailure: (reason) => {
        this._wakeWasArmed = false;
        this._setVoiceSnapshot({ wakeArmed: false, wakeFailure: reason });
        if (this._voiceSnapshot.status === "armed") this._setVoiceStatus("idle");
      },
    });

    this._wakeWasArmed = true;
    this._setVoiceSnapshot({ wakeArmed: true, wakeFailure: undefined });
    if (status === "idle" || status === "error") this._setVoiceStatus("armed");
    this._wakeListener.start();
  }

  /** @param remember false when the visitor turned it off themselves, so a
   *  finished session doesn't silently re-arm against their wishes. */
  _wakeDisarm(remember = true): void {
    if (!remember) this._wakeWasArmed = false;
    this._wakeListener?.destroy();
    this._wakeListener = null;
    this._setVoiceSnapshot({ wakeArmed: false });
    if (this._voiceSnapshot.status === "armed") this._setVoiceStatus("idle");
  }

  async _voiceConnect(): Promise<void> {
    if (!this.isInitialized) {
      console.error("[Voxide] Client not initialized. Call client.init() first.");
      this._setVoiceStatus("error");
      return;
    }
    if (this._voiceWs && this._voiceWs.readyState !== WebSocket.CLOSED) return;

    this._setVoiceSnapshot({ errorCode: undefined });
    this._setVoiceStatus("connecting");

    // Warm-up ping so a cold Render dyno is awake before the upgrade.
    const httpUrl = this.baseUrl
      ? `${this.baseUrl}/api/sdk/live`
      : typeof window !== "undefined"
      ? `${window.location.origin}/api/sdk/live`
      : "";
    try {
      if (httpUrl) await fetch(httpUrl, { method: "GET" });
    } catch {
      /* attempt WS anyway */
    }

    // Attaching a stable per-tab visitor id lets the server recognize "this is
    // the same visitor reconnecting" (voice stopped then continued by text, a
    // dropped connection, etc.) and seed the new session with their recent
    // conversation history server-side — see resolveLiveUrl and
    // voxide-ws-server's getRecentHistory.
    const wsUrl = resolveLiveUrl(this.baseUrl, this.publicKey, getVisitorId());
    const ws = new WebSocket(wsUrl);
    this._voiceWs = ws;

    ws.onopen = () => {
      this._setVoiceStatus("listening");
      this._voiceStartMic();
    };

    ws.onmessage = async (event) => {
      let msg: any;
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }
      try {
        switch (msg.type) {
          case "ready":
            // Older ws-server builds don't send sessionId; treat it as absent
            // rather than assuming, so feedback simply stays unavailable
            // instead of posting a bogus id.
            if (typeof msg.sessionId === "string" && msg.sessionId) {
              this._setVoiceSnapshot({ sessionId: msg.sessionId });
            }
            break;
          case "text": {
            const piece = msg.text || "";
            this._voicePendingAiText += piece;
            const aggregated = this._voicePendingAiText;
            this._emit("transcript", { role: "ai", text: aggregated, partial: true });
            this._setVoiceSnapshot({
              messages: upsertPartial(this._voiceSnapshot.messages, "ai", aggregated),
            });
            if (msg.turnComplete) {
              this._voicePendingAiText = "";
              this._emit("message", { role: "ai", text: aggregated });
              this._setVoiceSnapshot({ messages: finalizePartial(this._voiceSnapshot.messages, "ai") });
            }
            break;
          }
          case "text_user": {
            const piece = msg.text || "";
            this._voicePendingUserText += piece;
            const aggregated = this._voicePendingUserText;
            this._setVoiceSnapshot({
              messages: upsertPartial(this._voiceSnapshot.messages, "user", aggregated),
            });
            if (msg.turnComplete) {
              this._voicePendingUserText = "";
              this._emit("message", { role: "user", text: aggregated });
              this._setVoiceSnapshot({ messages: finalizePartial(this._voiceSnapshot.messages, "user") });
            }
            break;
          }
          // Authoritative end-of-turn. The server sends this on its own,
          // because Gemini's turnComplete arrives as a separate message with
          // no text attached — so relying on the flag riding along with a
          // "text" payload meant a turn was frequently never closed here, and
          // the next reply appended to the still-open bubble.
          //
          // Both roles are finalized: a turn ends the user's utterance and the
          // agent's reply together. Guarded on the pending buffers so that when
          // the flag DOES happen to arrive with the text, this doesn't emit a
          // duplicate "message" event.
          case "turn_complete": {
            if (this._voicePendingUserText) {
              this._emit("message", { role: "user", text: this._voicePendingUserText });
              this._voicePendingUserText = "";
            }
            this._flushPendingAiText();
            this._setVoiceSnapshot({ messages: finalizeAllPartials(this._voiceSnapshot.messages) });
            break;
          }
          case "audio":
            this._setVoiceStatus("speaking");
            this._voicePlayAudioChunk(msg.data);
            break;
          case "tool_call": {
            // A tool call lands mid-turn: Gemini can speak a remark before it
            // ("Checking the trending creators for you…"), call the tool, then
            // keep speaking after ("I've navigated..."). The server treats all
            // of that as one turn_complete-delimited turn (see server.js), so
            // without this the post-tool text would silently glue onto the
            // pre-tool bubble via _voicePendingAiText — same accumulator, no
            // boundary in between. Flush now so the pre-tool remark finalizes
            // as its own bubble and any post-tool remark starts a fresh one.
            this._flushPendingAiText();
            this._setVoiceStatus("executing");
            this._setVoiceSnapshot({ currentAction: msg.name });
            const currentState = this._getCurrentStateSnapshot();
            const result = await this._executeAction(msg.name, msg.args);
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: "tool_result", id: msg.id, name: msg.name, result, state: currentState }));
            }
            this._setVoiceSnapshot({ currentAction: null });
            this._setVoiceStatus("thinking");
            break;
          }
          case "interrupted":
            this._voiceStopPlayback();
            this._setVoiceStatus("listening");
            break;
          case "error":
            console.error("[Voxide WS] Server error:", msg.message);
            this._emit("error", msg.message);
            this._setVoiceSnapshot({ errorCode: msg.message === "usage_limit" ? "usage_limit" : undefined });
            this._setVoiceStatus("error");
            break;
        }
      } catch (e) {
        console.error("[Voxide WS] onmessage handler threw:", e);
      }
    };

    ws.onerror = (err) => {
      console.error("[Voxide WS] Error:", err);
      this._setVoiceStatus("error");
    };
    ws.onclose = () => {
      this._voiceDisconnect();
    };
  }

  async _voiceSendText(text: string): Promise<void> {
    if (!text?.trim()) return;
    // Defensive: a new user message unambiguously means any prior AI reply is
    // over, even if its turn_complete signal was delayed or dropped. Flush
    // now so this turn's text can never accumulate onto a stale leftover
    // string from the previous one (the same class of bug _flushPendingAiText
    // guards against around tool calls).
    this._flushPendingAiText();
    this._setVoiceSnapshot({
      messages: [...finalizeAllPartials(this._voiceSnapshot.messages), { role: "user", text }],
    });

    if (this._voiceWs?.readyState === WebSocket.OPEN) {
      const state = this._getCurrentStateSnapshot();
      this._setVoiceStatus("thinking");
      this._voiceWs.send(JSON.stringify({ type: "text_input", text, state }));
      return;
    }
    await this._voiceConnect();
    const ws = this._voiceWs;
    if (!ws) {
      this._setVoiceStatus("error");
      return;
    }
    const flush = () => {
      const state = this._getCurrentStateSnapshot();
      this._setVoiceStatus("thinking");
      ws.send(JSON.stringify({ type: "text_input", text, state }));
    };
    if (ws.readyState === WebSocket.OPEN) flush();
    else ws.addEventListener("open", flush, { once: true });
  }

  _voiceInterrupt(): void {
    this._voiceStopPlayback();
    if (this._voiceWs?.readyState === WebSocket.OPEN) {
      this._voiceWs.send(JSON.stringify({ type: "interrupt" }));
    }
    this._setVoiceStatus("listening");
  }

  /** Releases event listeners and ends any live voice session. Call when
   *  tearing down a client instance for good (not on a component remount —
   *  the whole point of the voice session living here is that it survives those). */
  destroy(): void {
    // Disarm before disconnecting: _voiceDisconnect() re-arms on its way out,
    // and a destroyed client must not leave a microphone open behind it.
    this._wakeDisarm(false);
    this._voiceDisconnect();
    (Object.keys(this._listeners) as VoxideEvent[]).forEach((k) => {
      this._listeners[k] = [];
    });
    this.actions.clear();
    this.middlewares = [];
  }
}

// Coalesce a streamed partial transcript into the trailing bubble for `role`,
// or start a new one — so a sentence streamed token-by-token renders as ONE
// growing bubble instead of a new one per chunk.
function upsertPartial(messages: VoxideMessage[], role: "user" | "ai", text: string): VoxideMessage[] {
  const last = messages[messages.length - 1];
  if (last && last.role === role && last.partial) {
    return [...messages.slice(0, -1), { ...last, text }];
  }
  return [...messages, { role, text, partial: true }];
}

/**
 * Closes every still-open bubble. Used at a turn boundary, where nothing can
 * still be streaming by definition.
 *
 * finalizePartial() below only inspects the LAST message, so in the normal
 * interleaving (user speaks → agent replies → turn ends) the user's bubble is
 * no longer last by the time the turn closes and would stay marked partial
 * forever. Harmless to render, but it leaves a bubble eligible for a future
 * append — the exact class of bug this whole path exists to prevent.
 */
function finalizeAllPartials(messages: VoxideMessage[]): VoxideMessage[] {
  if (!messages.some((m) => m.partial)) return messages; // preserve identity when nothing changed
  return messages.map((m) => (m.partial ? { ...m, partial: false } : m));
}

function finalizePartial(messages: VoxideMessage[], role: "user" | "ai"): VoxideMessage[] {
  const last = messages[messages.length - 1];
  if (last && last.role === role && last.partial) {
    return [...messages.slice(0, -1), { ...last, partial: false }];
  }
  return messages;
}
